import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiRefreshCw } from 'react-icons/fi';
import tributarioService from '../services/tributarioService';
import Loading from '../components/Loading';
import './ResumenIVAPage.css';

const ResumenIVAPage = () => {
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [anio, setAnio] = useState(new Date().getFullYear());

  const cargar = async () => {
    try {
      setLoading(true);
      const res = await tributarioService.resumenIVA.getAll({ periodo_anio: anio });
      const items = Array.isArray(res.data?.results) ? res.data.results : Array.isArray(res.data) ? res.data : [];
      setResumen({ periodos: items, total_debito: items.reduce((s, p) => s + parseFloat(p.iva_debito_fiscal || 0), 0), total_credito: items.reduce((s, p) => s + parseFloat(p.iva_credito_fiscal || 0), 0), total_a_pagar: items.reduce((s, p) => s + parseFloat(p.iva_a_pagar || 0), 0) });
    } catch { toast.error('Error al cargar resumen IVA'); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const periodos = resumen?.periodos || resumen?.results || [];

  return (
    <div className="resumen-iva-page">
      <div className="page-header">
        <h1>Resumen IVA</h1>
      </div>

      <div className="selector-bar">
        <div className="form-group">
          <label>Ano</label>
          <select value={anio} onChange={e => setAnio(parseInt(e.target.value))}>
            {[2024, 2025, 2026].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <button className="btn-primary" onClick={cargar}><FiRefreshCw /> Consultar</button>
      </div>

      {loading && <Loading />}

      {!loading && resumen && (
        <>
          {/* Dashboard */}
          <div className="iva-dashboard">
            <div className="iva-card debito">
              <div className="iva-card-label">IVA Debito Fiscal (Ventas)</div>
              <div className="iva-card-value">{formatCurrency(resumen.total_debito || periodos.reduce((s, p) => s + parseFloat(p.iva_debito_fiscal || 0), 0))}</div>
            </div>
            <div className="iva-card credito">
              <div className="iva-card-label">IVA Credito Fiscal (Compras)</div>
              <div className="iva-card-value">{formatCurrency(resumen.total_credito || periodos.reduce((s, p) => s + parseFloat(p.iva_credito_fiscal || 0), 0))}</div>
            </div>
            <div className="iva-card resultado">
              <div className="iva-card-label">IVA a Pagar / Remanente</div>
              <div className="iva-card-value">{formatCurrency(resumen.total_a_pagar || periodos.reduce((s, p) => s + parseFloat(p.iva_a_pagar || 0), 0))}</div>
            </div>
          </div>

          {/* Grafico simple de barras */}
          <div className="iva-chart">
            <h3>IVA Mensual {anio}</h3>
            <div className="chart-bars">
              {meses.map((mesNombre, i) => {
                const periodo = periodos.find(p => p.mes === i + 1) || {};
                const debito = parseFloat(periodo.iva_debito_fiscal || 0);
                const credito = parseFloat(periodo.iva_credito_fiscal || 0);
                const maxVal = Math.max(...periodos.map(p => Math.max(parseFloat(p.iva_debito_fiscal || 0), parseFloat(p.iva_credito_fiscal || 0))), 1);
                return (
                  <div key={i} className="chart-month">
                    <div className="chart-bar-group">
                      <div className="chart-bar debito" style={{ height: `${(debito / maxVal) * 120}px` }} title={`Debito: ${formatCurrency(debito)}`}></div>
                      <div className="chart-bar credito" style={{ height: `${(credito / maxVal) * 120}px` }} title={`Credito: ${formatCurrency(credito)}`}></div>
                    </div>
                    <span className="chart-label">{mesNombre.substring(0, 3)}</span>
                  </div>
                );
              })}
            </div>
            <div className="chart-legend">
              <span><span className="legend-dot debito"></span> Debito Fiscal</span>
              <span><span className="legend-dot credito"></span> Credito Fiscal</span>
            </div>
          </div>

          {/* Tabla detalle */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Mes</th>
                  <th style={{ textAlign: 'right' }}>IVA Debito (Ventas)</th>
                  <th style={{ textAlign: 'right' }}>IVA Credito (Compras)</th>
                  <th style={{ textAlign: 'right' }}>IVA a Pagar</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {periodos.length === 0 ? (
                  <tr><td colSpan="5" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Sin datos para este ano</td></tr>
                ) : periodos.map((p, i) => (
                  <tr key={i}>
                    <td><strong>{meses[p.mes - 1] || p.mes}</strong></td>
                    <td className="mono right">{formatCurrency(p.iva_debito_fiscal)}</td>
                    <td className="mono right">{formatCurrency(p.iva_credito_fiscal)}</td>
                    <td className="mono right bold" style={{ color: parseFloat(p.iva_a_pagar || 0) >= 0 ? '#dc2626' : '#059669' }}>
                      {formatCurrency(p.iva_a_pagar)}
                    </td>
                    <td><span className={`badge ${p.estado === 'pagado' ? 'badge-green' : 'badge-yellow'}`}>{p.estado || 'pendiente'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ResumenIVAPage;
